/**
 * GUMSHOE — InteractTarget resolution. Maps incoming 'player:interact'
 * targets onto case entities defensively: prefers the typed clueId/suspectId
 * fields, falls back to id conventions ('clue:<id>', 'accuse:<id>', raw id)
 * so integration with PLAYER/UI is forgiving. Owned by GAME.
 */
import type { InteractTarget } from '../core/types'
import { PHONE_BOOTH_INTERACT_IDS } from './data'

const CLUE_ID_PREFIX = 'clue:'
const ACCUSE_ID_PREFIX = 'accuse:'
const SUSPECT_ID_PREFIX = 'suspect:'
const PROP_ID_PREFIX = 'prop:'

const stripPrefix = (value: string, prefix: string): string =>
  value.startsWith(prefix) ? value.slice(prefix.length) : value

/** Resolve a target to a known clue id, or null if it isn't a clue we own. */
export const resolveClueId = (
  target: InteractTarget,
  knownClueIds: ReadonlySet<string>
): string | null => {
  if (target.clueId !== undefined && knownClueIds.has(target.clueId)) {
    return target.clueId
  }
  const fromId = stripPrefix(target.id, CLUE_ID_PREFIX)
  if (knownClueIds.has(fromId)) return fromId
  return null
}

/** Resolve a target to a known suspect id (an accusation), or null. */
export const resolveSuspectId = (
  target: InteractTarget,
  knownSuspectIds: ReadonlySet<string>
): string | null => {
  if (target.suspectId !== undefined && knownSuspectIds.has(target.suspectId)) {
    return target.suspectId
  }
  const fromAccuseId = stripPrefix(target.id, ACCUSE_ID_PREFIX)
  if (knownSuspectIds.has(fromAccuseId)) return fromAccuseId
  const fromSuspectId = stripPrefix(target.id, SUSPECT_ID_PREFIX)
  if (knownSuspectIds.has(fromSuspectId)) return fromSuspectId
  return null
}

/** True when the target is the street phone booth, however WORLD tagged it. */
export const isPhoneBoothTarget = (target: InteractTarget): boolean => {
  const normalized = stripPrefix(target.id.toLowerCase(), PROP_ID_PREFIX)
  return PHONE_BOOTH_INTERACT_IDS.has(normalized) || normalized.includes('phone')
}
